// Partner cockpit root layout. An impersonation/acting-as banner (B3-IMPERSONATION)
// surfaces the active tenant context. PLAN-C1.11. Rebuilt onto the shadcn design system
// (W34 PATH-B) for visual parity with the tenant app — the chrome now mirrors the tenant
// AppShell: a top app bar (wordmark + Partner badge + bell + user) over an underline nav
// strip with lucide icons. No i18n in partner-pages, so no NL/EN toggle (see report).
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from "@tanstack/react-query";
import { Bell, Briefcase, Building2, Inbox, LayoutGrid, LogOut, type LucideIcon, Search, ShieldCheck } from "lucide-react";
import { hasPermission, type PermissionCode } from "@sympllex/auth";
import { ApiError } from "@sympllex/api-client";
import { SessionProvider, useSession } from "../lib/session";
import { handleSessionExpired } from "../lib/auth";
import { AUTH_BASE } from "../lib/env";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// A 401 that survives the api-client's silent refresh-and-retry means the session is genuinely over →
// re-auth through AuthKit. Don't retry 4xx; retry transient network/5xx once.
const onAuthError = (err: unknown) => { if (err instanceof ApiError && err.status === 401) handleSessionExpired(); };
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && failureCount < 1,
    },
  },
});

// The partner nav, mirroring the tenant persona tab-strip: a lucide icon + label per item,
// with an active-underline treatment. `perm` gates the item like the tenant <Can> wrapping.
type NavItem = { to: string; label: string; icon: LucideIcon; perm?: PermissionCode };
const NAV: NavItem[] = [
  { to: "/", label: "Portfolio", icon: LayoutGrid },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/programs", label: "Programs", icon: Briefcase },
  { to: "/search", label: "Search", icon: Search },
  { to: "/settings", label: "Firm", icon: Building2 },
  { to: "/engagements", label: "Engagements", icon: ShieldCheck, perm: "partner_relationship.read" },
];

// Longest-prefix active match (mirrors the tenant useActivePersona): the item whose route is
// the deepest prefix of the current path, falling back to the Portfolio home.
function useActiveNav(): NavItem {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const byItem = NAV.find((i) => i.to !== "/" && path.startsWith(i.to));
  return byItem ?? NAV[0]!;
}

function TopNav() {
  const session = useSession();
  const active = useActiveNav();
  const can = (perm?: PermissionCode) => !perm || hasPermission(session, perm);
  const logout = async () => {
    await fetch(`${AUTH_BASE}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    window.location.href = `${AUTH_BASE}/auth/login`;
  };

  return (
    <header className="sticky top-0 z-20 border-b bg-card">
      {/* Top app bar — wordmark + Partner badge, with the bell + user cluster right-aligned. */}
      <div className="flex h-14 items-center gap-4 px-5">
        <Link to="/" className="flex shrink-0 items-center gap-2 whitespace-nowrap text-[15px] font-semibold tracking-tight text-primary">
          Aquino Compliance
          <Badge variant="secondary" className="ml-1 font-normal">Partner</Badge>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          {/* Notification bell — placeholder (no partner notifications endpoint), styled like the tenant's. */}
          <Button variant="ghost" size="icon" aria-label="Notifications" disabled>
            <Bell className="size-4" />
          </Button>
          {/* User — no dropdown-menu component in partner-pages, so the email button logs out directly. */}
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={logout}>
            <span className="max-w-[16rem] truncate">{session.user.email}</span>
            <LogOut className="size-3.5" />
          </Button>
        </div>
      </div>
      {/* Nav strip — persona-tab styling: lucide icon + active-underline (matches tenant AppShell). */}
      <nav className="flex gap-1 px-3" aria-label="Partner cockpit">
        {NAV.filter((i) => can(i.perm)).map((i) => {
          const Icon = i.icon;
          const isActive = i.to === active.to;
          return (
            <Link key={i.to} to={i.to}
              className={cn("flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm",
                isActive ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
              <Icon className="size-4" />{i.label}
            </Link>
          );
        })}
      </nav>
    </header>
  );
}

function MandateBanner() {
  const session = useSession();
  if (!session.actingAsPartnerUserId) return null;
  return (
    <div role="status" className="border-b bg-blue-900 px-5 py-2 text-sm text-white">
      <div className="mx-auto flex max-w-6xl items-center gap-2">
        <ShieldCheck className="size-4" aria-hidden />
        <span>
          Acting on behalf of a client tenant · context{" "}
          <code className="rounded bg-white/15 px-1 py-0.5 text-xs">{session.activeTenantId.slice(0, 8)}…</code>
        </span>
      </div>
    </div>
  );
}

export function RootLayout() {
  return (
    <SessionProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <div className="min-h-screen bg-background text-foreground">
            <TopNav />
            <MandateBanner />
            <main className="mx-auto max-w-6xl px-5 py-8">
              <Outlet />
            </main>
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
